import {SyncableStore, SyncPayload} from "@/stores/plugins/sync/types";
import {getEnabledStoreIds} from "@/stores/plugins/sync/helper";
import {SYNC_CONFIG} from "@/stores/plugins/sync/config";

let isInitialSyncComplete = false;
const pendingSyncUpdates = new Map<string, SyncPayload>();
const syncedStores = new Map<string, SyncableStore>();

/**
 * Sets up the slave window synchronization by applying pending updates,
 * setting up the store update listener, and requesting the initial sync.
 */
export const setupSlaveWindowSync = (store: SyncableStore): void => {
    applyPendingUpdates(store);
    setupStoreUpdateListener();

    syncedStores.set(store.$id, store);
    requestInitialSync();
};

/**
 * Applies any pending updates to the store if they exist.
 */
const applyPendingUpdates = (store: SyncableStore): void => {
    if (!pendingSyncUpdates.has(store.$id)) {
        return;
    }

    const pendingUpdate = pendingSyncUpdates.get(store.$id);

    if (!pendingUpdate || !store.updateFromSync) {
        return;
    }

    try {
        store.updateFromSync(pendingUpdate.state);
        pendingSyncUpdates.delete(store.$id);
    } catch {
        pendingSyncUpdates.delete(store.$id);
    }
};

/**
 * Sets up the store update listener for the slave window.
 * This listener will handle incoming store updates from the main process.
 */
const setupStoreUpdateListener = (): void => {
    if (window._storeUpdateHandlerSetup) {
        return;
    }

    window._storeUpdateHandlerSetup = true;

    window.ipcRenderer?.onStoreUpdate((data: SyncPayload) => {
        handleIncomingStoreUpdate(data);
    });
};

/**
 * Handles incoming store updates from the main process.
 * If the store is found and has an update method, it applies the update.
 * If the initial sync is not complete, it stores the update for later.
 */
const handleIncomingStoreUpdate = (data: SyncPayload): void => {
    const targetStore = syncedStores.get(data.storeId);

    if (targetStore && targetStore.updateFromSync) {
        targetStore.updateFromSync(data.state);
    } else if (!isInitialSyncComplete) {
        pendingSyncUpdates.set(data.storeId, data);
    }
};

/**
 * Requests the initial synchronization of stores from the main process.
 * This is done after a short delay to ensure that the slave window is ready.
 */
const requestInitialSync = (): void => {
    if (window._initialSyncRequested) {
        return;
    }

    window._initialSyncRequested = true;

    setTimeout(() => {
        isInitialSyncComplete = true;

        const storeIdsToSync = getEnabledStoreIds(SYNC_CONFIG.stores);

        window.ipcRenderer?.requestSync(storeIdsToSync);
    }, 150);
};

export const getSlaveWindowSyncedStores = (): Map<string, SyncableStore> => {
    return syncedStores;
};
